import { useEffect, useRef } from "react";
import { useCountdown } from "../lib/clock";
import { play } from "../lib/sfx";

interface Props {
  deadline: number | null | undefined;
  total: number; // phaseMs
  offset: number;
  paused?: boolean;
  color?: string;
  /** tick sounds during the last N seconds (0 = silent) */
  tickLast?: number;
  onLowTime?(): void;
  big?: boolean;
}

/** Progress bar + seconds for a server deadline. Ticks in the last seconds. */
export function TimerBar({ deadline, total, offset, paused, color = "var(--color-flame)", tickLast = 5, onLowTime, big }: Props) {
  const left = useCountdown(deadline, offset, paused, 100);
  const lastSec = useRef<number | null>(null);
  const warned = useRef(false);
  const s = left == null ? null : Math.ceil(left / 1000);
  useEffect(() => {
    if (s == null) return;
    if (lastSec.current !== s) {
      if (tickLast && s > 0 && s <= tickLast && lastSec.current !== null) play(s <= 3 ? "tock" : "tick", { volume: 0.7 });
      if (s === 10 && !warned.current) {
        warned.current = true;
        onLowTime?.();
      }
      lastSec.current = s;
    }
  }, [s, tickLast, onLowTime]);
  useEffect(() => {
    warned.current = false;
  }, [deadline]);
  if (left == null) return null;
  const frac = total > 0 ? Math.min(1, left / total) : 0;
  const urgent = s != null && s <= 5;
  return (
    <div className="flex items-center gap-3" aria-live="off">
      <div className={`relative flex-1 overflow-hidden rounded-full bg-ground-3 ${big ? "h-4" : "h-2.5"}`}>
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${frac * 100}%`, background: urgent ? "var(--color-candy)" : color, transition: "width 0.12s linear" }}
        />
      </div>
      <span className={`tabular min-w-[2.6ch] text-right font-black ${big ? "text-3xl" : "text-xl"} ${urgent ? "urgent text-candy" : ""}`}>{s}</span>
    </div>
  );
}
